import { useState, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import LinearProgress from '@mui/material/LinearProgress';
import DisabledFieldTooltip from './DisabledFieldTooltip';

const createOptionKey = (option, index = 0) => {
  return [option?.value ?? '', option?.label ?? '', option?.type ?? '', index].join('__');
};

const normalizeOptions = (options = []) => {
  return options.map((option, index) => ({
    ...option,
    _optionKey: option?._optionKey ?? createOptionKey(option, index),
  }));
};

const getFallbackLabel = (selectedLabelValue, propValue, effectiveSelectedValue) => {
  if (typeof selectedLabelValue === 'string' && selectedLabelValue.trim().length > 0) {
    return selectedLabelValue;
  }

  if (!Array.isArray(propValue) && typeof propValue === 'object' && typeof propValue?.label === 'string') {
    if (propValue.label.trim().length > 0) return propValue.label;
  }

  if (typeof effectiveSelectedValue === 'string' || typeof effectiveSelectedValue === 'number') {
    return String(effectiveSelectedValue);
  }

  return '';
};

const AutoCompleteSelectServiceUI = (props) => {
  const {
    id,
    name,
    label,
    placeholder,
    listType,
    selectedValue,
    selectedLabelValue,
    multiple,
    value: propValue,
    variant,
    isOptionEqualToValue,
    helperText,
    service,
    serviceRequestBuilder,
    serviceResponseMapper,
    requestDebounceMs = 300,
    pageSize = 25,
    onError,
  } = props;

  const [list, setList] = useState([]);
  const [defaultList, setDefaultList] = useState([]);
  const [value, setValue] = useState(multiple ? [] : null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const lastFetchRequestKeyRef = useRef('');
  const resolvedSelectedValueRef = useRef(null);
  const previousSelectedValueRef = useRef(undefined);
  const previousSelectedLabelValueRef = useRef(undefined);
  const suppressNextFetchRef = useRef(false);

  useEffect(() => {
    // New form field instance should not reuse previous field's dedupe key.
    lastFetchRequestKeyRef.current = '';
    resolvedSelectedValueRef.current = null;
  }, [id, name]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, requestDebounceMs);

    return () => {
      clearTimeout(timerId);
    };
  }, [inputValue, requestDebounceMs]);

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }

    fetchData(debouncedInputValue);
  }, [debouncedInputValue, pageSize]);

  useEffect(() => {
    resolvedSelectedValueRef.current = null;
  }, [selectedValue, propValue]);

  useEffect(() => {
    setList(defaultList);
  }, [defaultList]);

  useEffect(() => {
    const effectiveSelectedValue = selectedValue ?? propValue;
    const normalizedInputValue = String(inputValue ?? '').trim();
    const selectedValueChanged = previousSelectedValueRef.current !== effectiveSelectedValue;
    const selectedLabelChanged = previousSelectedLabelValueRef.current !== selectedLabelValue;

    previousSelectedValueRef.current = effectiveSelectedValue;
    previousSelectedLabelValueRef.current = selectedLabelValue;

    // While user is actively searching, do not force-restore previous selection,
    // unless selected value or selected label was changed externally.
    if (normalizedInputValue.length > 0 && !selectedValueChanged && !selectedLabelChanged) {
      return;
    }

    if (
      !multiple &&
      effectiveSelectedValue &&
      typeof effectiveSelectedValue === 'object' &&
      !Array.isArray(effectiveSelectedValue)
    ) {
      const normalizedValue = normalizeOptions([effectiveSelectedValue])[0];
      const findedListItem = defaultList.find((option) =>
        (isOptionEqualToValue ?? ((optionItem, valueItem) => optionItem?.value === valueItem?.value))(
          option,
          normalizedValue
        )
      );

      setList(findedListItem ? defaultList : [...defaultList, normalizedValue]);
      setValue(findedListItem || normalizedValue);
      return;
    }

    const findedListItem = defaultList.find((p) => p.value === effectiveSelectedValue);

    if (findedListItem && selectedLabelValue && findedListItem.label !== selectedLabelValue) {
      const updatedSelectedOption = {
        ...findedListItem,
        label: selectedLabelValue,
      };

      setDefaultList((prevList) =>
        prevList.map((option) => (option.value === effectiveSelectedValue ? updatedSelectedOption : option))
      );
      setList((prevList) =>
        prevList.map((option) => (option.value === effectiveSelectedValue ? updatedSelectedOption : option))
      );
      setValue(updatedSelectedOption);
      return;
    }

    if (
      !findedListItem &&
      ((typeof effectiveSelectedValue === 'number' && effectiveSelectedValue > 0) ||
        (typeof effectiveSelectedValue === 'string' && effectiveSelectedValue.trim().length > 0))
    ) {
      fetchSelectedValueData(effectiveSelectedValue);

      const newVal = {
        value: effectiveSelectedValue,
        label:
          selectedLabelValue ??
          (!Array.isArray(propValue) && typeof propValue === 'object' ? propValue?.label ?? '' : ''),
        _resolvedSelected: true,
        _optionKey: createOptionKey(
          {
            value: effectiveSelectedValue,
            label:
              selectedLabelValue ??
              (!Array.isArray(propValue) && typeof propValue === 'object' ? propValue?.label ?? '' : ''),
          },
          defaultList.length
        ),
      };
      setList([newVal]);
      setValue(newVal);
    } else {
      if (multiple) {
        setList(defaultList);
        setValue(Array.isArray(effectiveSelectedValue) ? effectiveSelectedValue : []);
      } else {
        setList(normalizedInputValue.length > 0 ? defaultList : findedListItem ? [findedListItem] : []);
        setValue(findedListItem || null);
      }
    }
  }, [selectedValue, selectedLabelValue, defaultList, multiple, propValue, isOptionEqualToValue, inputValue]);

  const mapResponseToOptions = (response, searchText, requestPayload) => {
    const body = response;

    const formattedData = body.map((item, index) => {
      const source = item?.item ?? item;

      if (typeof serviceResponseMapper === 'function') {
        return serviceResponseMapper(source, {
          index,
          listType,
          searchText,
          request: requestPayload,
        });
      }

      const labelFromSource =
        (listType?.name ? source?.[listType.name] : undefined) ??
        source?.label ??
        source?.name ??
        [source?.itemCode, source?.itemName].filter(Boolean).join(' - ');

      const valueFromSource =
        (listType?.id ? source?.[listType.id] : undefined) ??
        source?.value ??
        source?.itemCode ??
        source?.code ??
        source?.id;

      return {
        label: labelFromSource || String(valueFromSource ?? ''),
        value: valueFromSource,
        ...(listType?.type ? { type: source?.[listType.type] } : source?.type ? { type: source.type } : {}),
        ...(source?.icon ? { icon: source.icon } : {}),
      };
    });

    return normalizeOptions(formattedData.filter((option) => option?.value !== undefined && option?.value !== null));
  };

  const fetchSelectedValueData = (effectiveSelectedValue) => {
    if (typeof service !== 'function') {
      return;
    }

    if (resolvedSelectedValueRef.current === effectiveSelectedValue) {
      return;
    }

    resolvedSelectedValueRef.current = effectiveSelectedValue;

    const serviceRequest =
      typeof serviceRequestBuilder === 'function'
        ? serviceRequestBuilder({
            searchText: '',
            pageSize,
            selectedValue: effectiveSelectedValue,
            mode: 'resolveSelectedValue',
            props,
          })
        : {
            searchText: '',
            pageSize,
            selectedValue: effectiveSelectedValue,
            mode: 'resolveSelectedValue',
          };

    if (!serviceRequest) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    Promise.resolve(service(serviceRequest))
      .then((response) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        const resolvedOptions = mapResponseToOptions(response, '', serviceRequest);
        if (!resolvedOptions.length) {
          return;
        }

        const equalsOption =
          isOptionEqualToValue ?? ((optionItem, valueItem) => optionItem?.value === valueItem?.value);

        const targetValueOption = { value: effectiveSelectedValue };
        const resolvedSelectedOption =
          resolvedOptions.find((option) => equalsOption(option, targetValueOption)) ??
          resolvedOptions.find((option) => option?.value === effectiveSelectedValue) ??
          // Resolve endpoint is expected to return one exact item; keep first result as fallback.
          (resolvedOptions.length === 1
            ? {
                ...resolvedOptions[0],
                value: resolvedOptions[0]?.value ?? effectiveSelectedValue,
                _resolvedSelected: true,
              }
            : null) ??
          null;

        if (!resolvedSelectedOption) {
          return;
        }

        const normalizedResolvedSelectedOption = {
          ...resolvedSelectedOption,
          _resolvedSelected: true,
        };

        setDefaultList([normalizedResolvedSelectedOption]);
      })
      .catch((error) => {
        if (typeof onError === 'function') {
          onError(error);
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  };

  const fetchData = (searchText = '', { forceWhenEmpty = false } = {}) => {
    if (typeof service !== 'function') {
      setDefaultList([]);
      return;
    }

    const normalizedSearchText = String(searchText ?? '').trim();
    if (!normalizedSearchText && !forceWhenEmpty) {
      return;
    }

    const serviceRequest =
      typeof serviceRequestBuilder === 'function'
        ? serviceRequestBuilder({ searchText: normalizedSearchText, pageSize, mode: 'search', props })
        : { searchText: normalizedSearchText, pageSize, mode: 'search' };

    if (!serviceRequest) {
      return;
    }

    const requestKey = JSON.stringify(serviceRequest);
    if (lastFetchRequestKeyRef.current === requestKey) {
      return;
    }

    lastFetchRequestKeyRef.current = requestKey;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    Promise.resolve(service(serviceRequest))
      .then((response) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setDefaultList(mapResponseToOptions(response, searchText, serviceRequest));
      })
      .catch((error) => {
        if (typeof onError === 'function') {
          onError(error);
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      const selectedInputValue = event.target?.value?.trim();
      if (selectedInputValue) {
        const exactOption = list.find(
          (opt) => String(opt.label).trim() === selectedInputValue || String(opt.value).trim() === selectedInputValue
        );

        if (exactOption) {
          if (multiple) {
            const current = Array.isArray(value) ? value : [];
            const exists = current.some((opt) => opt.value === exactOption.value);
            const newValue = exists ? current : [...current, exactOption];
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, newValue);
            }
          } else {
            setValue(exactOption);
            if (props.onChange) {
              props.onChange(event, exactOption);
            }
          }
        }
      }
    }

    if (props.onKeyDown) {
      props.onKeyDown(event);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (props.onChange) {
      props.onChange(event, newValue);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') {
      suppressNextFetchRef.current = true;
      // Keep selected option label visible in the input after selection.
      setInputValue(newInputValue ?? '');
    } else if (reason === 'input' || reason === 'clear') {
      setInputValue(newInputValue ?? '');
    }

    if (props.onInputChange) {
      props.onInputChange(event, newInputValue, reason);
    }
  };

  const handleOpen = (event) => {
    // Open should not use the rendered label text as search input.
    // Request builder can resolve by selected value when search text is empty.
    fetchData('', { forceWhenEmpty: true });

    if (props.onOpen) {
      props.onOpen(event);
    }
  };

  const tooltipTitle = props.disabled
    ? multiple
      ? (Array.isArray(value) ? value : []).map((item) => item?.label ?? '').filter(Boolean).join('\n')
      : value?.label ?? getFallbackLabel(selectedLabelValue, propValue, selectedValue ?? propValue)
    : '';

  return (
    <DisabledFieldTooltip disabled={props.disabled} title={tooltipTitle} className="z-50 w-full">
        <Autocomplete
          {...props}
          clearOnEscape
          id={id}
          name={name ?? id}
          value={value}
          options={list}
          loading={loading}
          inputValue={inputValue}
          getOptionLabel={(option) => option?.label ?? ''}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onOpen={handleOpen}
          onKeyDown={handleKeyDown}
          isOptionEqualToValue={isOptionEqualToValue ?? ((option, val) => option?.value === val?.value)}
          sx={{ width: '100%' }}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              sx: {
                width: 'auto !important',
                maxWidth: '100vw',
                '& .MuiAutocomplete-paper': {
                  width: 'max-content',
                  minWidth: 150,
                  maxWidth: 'inherit',
                  whiteSpace: 'normal',
                },
              },
            },
          }}
          filterOptions={(availableOptions) => availableOptions}
          renderInput={(params) => {
            const selectedIcon = !multiple && value?.icon ? value.icon : null;
            return (
              <>
                <TextField
                  {...params}
                  variant={variant ?? 'standard'}
                  label={label}
                  placeholder={placeholder}
                  helperText={helperText}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedIcon ? (
                      <InputAdornment position="start">{selectedIcon}</InputAdornment>
                    ) : (
                      params.InputProps.startAdornment
                    ),
                  }}
                />
                {loading ? <LinearProgress sx={{ mt: 0.5 }} /> : null}
              </>
            );
          }}
          renderOption={(optionProps, option) => {
            const { key, ...restOptionProps } = optionProps;
            return (
              <li {...restOptionProps} key={option?._optionKey ?? key}>
                {option.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
                {option.label}
              </li>
            );
          }}
        />
    </DisabledFieldTooltip>
  );
};

export default AutoCompleteSelectServiceUI;
